/* eslint-disable react-hooks/exhaustive-deps */
import React, { useMemo } from 'react';
import * as THREE from 'three';
import { useThree } from '@react-three/fiber';

import { const_var, params } from '../../../../../../../redux/reducers/reducer';
import { delModel, extrudeSettings, roofPattern, textureAnisotropy, wainscotTrimModel } from '../../../../../../../utils/Functions';
import { CSG } from '../../../../../../../assets/libs/csg-lib';
import { MaterialOuterWallNew, MaterialWainScotTrim, MaterialWainScotTrimNew, MaterialWainScotWallNew } from '../../../../../CustomMaterial';

import { createSidingTextureObject } from '../../../../../../../redux/reducers/utils';

const OutsideWallModel = () => {
    const { gl } = useThree();
    
    const canvasElement = document.getElementsByClassName("buildingcanvas")
    const isPattern = (canvasElement != undefined && canvasElement[0]?.clientWidth > 1050 && !params.p_v_w) ? true : false
    // const texture = const_var.wallTextureObj[params.wallTexture];

    const wallModel = useMemo(() => {
        const leftHeight = (params.singleSlope && params.freeStandingSide === "left") ? params.p_h - ((params.p_r_p / 12) * params.p_w) : params.p_h
        const rightHeight = (params.singleSlope && params.freeStandingSide === "right") ? params.p_h + ((params.p_r_p / 12) * params.p_w) : params.p_h
        
        let model = new THREE.Shape();

        if (params.p_r_s === 1) {            
            model.moveTo(-params.p_w / 2 + 0.02, params.p_h);
            model.lineTo(-params.p_w / 2 + 0.02, params.p_h + const_var.roofRegularDst);
            model.quadraticCurveTo( -params.p_w / 2 + 0.02, params.p_h + const_var.roofRegularDst * 1.8 + (const_var.roofRegularDst * params.p_r_p / 12) / 10,
                                                -params.p_w / 2 + 0.02 + const_var.roofRegularDst, params.p_h + const_var.roofRegularDst * 2 + const_var.roofRegularDst * params.p_r_p / 12);
            model.lineTo(0, params.p_h + ((params.p_r_p / 12) * (params.p_w/2)) + const_var.roofRegularDst * 2 - 0.03);
            model.lineTo(params.p_w / 2 - 0.02 - const_var.roofRegularDst, params.p_h + const_var.roofRegularDst * 2 + const_var.roofRegularDst * params.p_r_p / 12);
            model.quadraticCurveTo( params.p_w / 2 - 0.02, params.p_h + const_var.roofRegularDst * 1.8 + (const_var.roofRegularDst * params.p_r_p / 12) / 10,
                                    params.p_w / 2 - 0.02, params.p_h + const_var.roofRegularDst);
            model.lineTo(params.p_w / 2 - 0.02, params.p_h);

            model.lineTo(params.p_w / 2 - 0.02, 0.01);
            model.lineTo(-params.p_w / 2 + 0.02, 0.01);    
        } else if (params.p_r_s === 2 || params.p_r_s === 3) {
            model.moveTo(-params.p_w / 2, leftHeight - 0.001);
            if(!params.singleSlope) model.lineTo(0, params.p_h + (params.p_r_p / 12) * params.p_w / 2);
            model.lineTo(params.p_w / 2, rightHeight - 0.01);
            model.lineTo(params.p_w / 2, 0.01);
            model.lineTo(-params.p_w / 2, 0.01);
        }
        model.closePath()

        return model;
    
    }, [params.p_w, params.p_h, params.p_r_s, params.p_r_p, params.singleSlope, params.freeStandingSide, params.p_w_c_n])

    const mainWallModel = useMemo(() => {    
        const regularHeight = params.p_r_s == 1 ? const_var.roofRegularDst * 2 : 0
        
        /************** cutting hole using CSG ***************/
        let verticalHorizontalPos = {"x": params.p_w / -2,"y" : (params.p_w_c_n ? 2.935 : 0), "z" : params.p_d / -2 + parseInt(params.p_u_t) + 0.01}
        let verticalHorizontalRot = {"x":0,"y" : Math.PI / 2,"z" : Math.PI / 2}
        let patternLength =  params.p_h + ((params.p_r_p / 12) * (params.p_w/2)) + regularHeight + (params.p_w_c_n ? -2.935 : 0)
        let patternDepth = params.p_w

        const meshWallModel = new THREE.Mesh(new THREE.ExtrudeGeometry(roofPattern(patternLength,isPattern), extrudeSettings(patternDepth)))
        const meshDelModel = new THREE.Mesh(new THREE.ExtrudeGeometry(delModel('backStorageFront', 'wall'), extrudeSettings(1)))
        const meshRightOffsetWithDoorDelModel = new THREE.Mesh(new THREE.ExtrudeGeometry(delModel('right', 'wall'), extrudeSettings(1)))
        const meshLeftOffsetWithDoorDelModel = new THREE.Mesh(new THREE.ExtrudeGeometry(delModel('left', 'wall'), extrudeSettings(1)))

        let meshWallModel_Temp = meshWallModel.clone();
        meshWallModel_Temp.position.set(verticalHorizontalPos.x,verticalHorizontalPos.y,verticalHorizontalPos.z);
        meshWallModel_Temp.rotation.set(verticalHorizontalRot.x,verticalHorizontalRot.y,verticalHorizontalRot.z);
        meshWallModel_Temp.updateMatrixWorld();

        const meshGableDelModel = new THREE.Mesh(new THREE.ExtrudeGeometry(wallModel, extrudeSettings(1)))
        let meshGableDelModel_Temp = meshGableDelModel.clone();
        meshGableDelModel_Temp.position.set(0,0,params.p_d / -2 + parseInt(params.p_u_t) - 0.5);
        meshGableDelModel_Temp.updateMatrixWorld();

        // mesh to cut the gable
        let meshDelModel_Temp = meshDelModel.clone();
        meshDelModel_Temp.position.set(0, 0, params.p_d / -2 + parseInt(params.p_u_t) - 0.5);
        meshDelModel_Temp.updateMatrixWorld();

        let meshRightOffsetWithDoorDelModel_Temp = meshRightOffsetWithDoorDelModel.clone();
        meshRightOffsetWithDoorDelModel_Temp.position.set(params.p_w / 2 + 0.75, 0, -params.endStoragePositionZ);
        meshRightOffsetWithDoorDelModel_Temp.rotateY(-Math.PI / 2)
        meshRightOffsetWithDoorDelModel_Temp.updateMatrixWorld();

        let meshLeftOffsetWithDoorDelModel_Temp = meshLeftOffsetWithDoorDelModel.clone();
        meshLeftOffsetWithDoorDelModel_Temp.position.set(-params.p_w / 2 + 0.25, 0, -params.endStoragePositionZ);
        meshLeftOffsetWithDoorDelModel_Temp.rotateY(-Math.PI / 2)
        meshLeftOffsetWithDoorDelModel_Temp.updateMatrixWorld();

        const meshWallModel_CSG = CSG.fromMesh(meshWallModel_Temp);
        const meshDelModel_CSG = CSG.fromMesh(meshDelModel_Temp);
        const meshRightOffsetWithDoorDelModel_CSG = CSG.fromMesh(meshRightOffsetWithDoorDelModel_Temp);
        const meshLeftOffsetWithDoorDelModel_CSG = CSG.fromMesh(meshLeftOffsetWithDoorDelModel_Temp);

        const meshGableDelModel_CSG = CSG.fromMesh(meshGableDelModel_Temp);

        const wallModel_CSG = meshWallModel_CSG.subtract(meshDelModel_CSG);
        const GableModel_CSG = wallModel_CSG.intersect(meshGableDelModel_CSG);
        const rightWallDel_CSG = GableModel_CSG.subtract(meshRightOffsetWithDoorDelModel_CSG);
        const leftWallDel_CSG = rightWallDel_CSG.subtract(meshLeftOffsetWithDoorDelModel_CSG);

        const CSGWallModel = CSG.toMesh(leftWallDel_CSG,new THREE.Matrix4())

        /************** cutting hole in wainscot using CSG ***************/
        let patternDepthWS = params.p_w
        let wainscotVerticalHorizontalPos = {"x": params.p_w / -2,"y" :0, "z" : params.p_d / -2 + parseInt(params.p_u_t) + 0.01}
        let wainscotVerticalHorizontalRot ={"x":0,"y" : Math.PI / 2,"z" : Math.PI / 2}
        let lengthPattern = 3
        let depthPattern = patternDepthWS

        const meshWainscotModel = new THREE.Mesh(new THREE.ExtrudeGeometry(roofPattern(lengthPattern,isPattern && params.wainscotTexture == "none"), extrudeSettings(depthPattern)))
        let meshWainscotModel_Temp = meshWainscotModel.clone();
        meshWainscotModel_Temp.position.set(wainscotVerticalHorizontalPos.x,wainscotVerticalHorizontalPos.y,wainscotVerticalHorizontalPos.z);
        meshWainscotModel_Temp.rotation.set(wainscotVerticalHorizontalRot.x,wainscotVerticalHorizontalRot.y,wainscotVerticalHorizontalRot.z);
        meshWainscotModel_Temp.updateMatrixWorld();

        const meshWainscotModel_CSG = CSG.fromMesh(meshWainscotModel_Temp);

        const wainscotModel_CSG = meshWainscotModel_CSG.subtract(meshDelModel_CSG);
        const rightWallWainscotDel_CSG = wainscotModel_CSG.subtract(meshRightOffsetWithDoorDelModel_CSG);
        const leftWallWainscotDel_CSG = rightWallWainscotDel_CSG.subtract(meshLeftOffsetWithDoorDelModel_CSG);

        // const wainscotModelCSG = wainscotModel_CSG.intersect(meshGableDelModel_CSG);
        const CSGWainscotModel = CSG.toMesh(
            leftWallWainscotDel_CSG,
            new THREE.Matrix4()
        )

        /************** cutting hole in Wainscot Trim using CSG ***************/

        const meshWainscotTrimModel = new THREE.Mesh(new THREE.ExtrudeGeometry(wainscotTrimModel(0.14, 0.17), extrudeSettings(params.p_w)))
        let meshWainscotTrimModel_Temp = meshWainscotTrimModel.clone();
        meshWainscotTrimModel_Temp.position.x = params.p_w / 2;
        meshWainscotTrimModel_Temp.position.y = 3
        meshWainscotTrimModel_Temp.position.z = -params.p_d / 2 + parseInt(params.p_u_t)+0.0025
        meshWainscotTrimModel_Temp.rotation.y = Math.PI/-2
        meshWainscotTrimModel_Temp.updateMatrixWorld();

        const meshWainscotTrimModel_CSG = CSG.fromMesh(meshWainscotTrimModel_Temp);

        const wainscotTrimModel_CSG = meshWainscotTrimModel_CSG.subtract(meshDelModel_CSG);
        const leftTrimDelModel_CSG = wainscotTrimModel_CSG.subtract(meshLeftOffsetWithDoorDelModel_CSG);
        const rightTrimDelModel_CSG = leftTrimDelModel_CSG.subtract(meshRightOffsetWithDoorDelModel_CSG);

        const CSGWainscotTrimModel = CSG.toMesh(
            rightTrimDelModel_CSG,
            new THREE.Matrix4()
        )

        return {
            wall: CSGWallModel.geometry,
            wainscotWall: CSGWainscotModel.geometry,
            wTrim: CSGWainscotTrimModel.geometry
        }
    }, [params.wallTexture,params.wainscotTexture,params.p_w, params.endStoragePositionZ, params.p_d, params.p_u_t, params.p_v_w, params.p_h, params.p_r_p, params.p_r_s, wallModel,params.p_w_c_n, const_var.newDoorsArray, isPattern])

    return (
        <group position={[0, 0, 0.17]}>
            {mainWallModel.wall &&
                <mesh name="c_b_f_s_w" geometry={mainWallModel.wall} position={[0, 0, 0.05]} renderOrder={1}>
                    <MaterialOuterWallNew isPattern={isPattern} visibleSection={"center"}  />
                </mesh>
            }
            {params.p_w_c_n === true && mainWallModel.wainscotWall &&
                <mesh name="c_b_f_s_w_wainscot" geometry={mainWallModel.wainscotWall} position={[0, 0, 0.051]} renderOrder={1}>
                    <MaterialWainScotWallNew isPattern={isPattern} visibleSection={"center"} />
                </mesh>
            }
            {params.p_w_c_n === true && mainWallModel.wTrim &&
                <mesh name="c_b_f_s_w_wainscotRightTrim" geometry={mainWallModel.wTrim} position={[0, 0, 0.0525]}>
                    <MaterialWainScotTrimNew visibleSection={"center"}  />
                </mesh>
            }
        </group>
    )
}

export default OutsideWallModel